import * as React from "react";

import { setActiveLocale } from "@/i18n/active-locale";

type Messages = Record<string, unknown>;
type TranslationValues = Record<string, string | number>;

type IntlContextValue = {
  locale: string;
  messages: Messages;
};

const IntlContext = React.createContext<IntlContextValue>({
  locale: "fr",
  messages: {},
});

export function NextIntlClientProvider({
  locale,
  messages,
  children,
}: {
  locale: string;
  messages: Messages;
  children: React.ReactNode;
}) {
  // Keep the module-level mirror in sync so non-React utilities (date formatters, selectors)
  // format in the active language. Set during render so it's correct before children paint.
  setActiveLocale(locale);
  const value = React.useMemo(() => ({ locale, messages }), [locale, messages]);
  return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
}

function lookup(messages: Messages, keyPath: string) {
  const segments = keyPath.split(".");
  let current: unknown = messages;
  for (const segment of segments) {
    if (!current || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function interpolate(template: string, values?: TranslationValues) {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = values[key];
    return value === undefined ? `{${key}}` : String(value);
  });
}

export function useLocale() {
  const context = React.useContext(IntlContext);
  return context.locale;
}

export function useMessages() {
  const context = React.useContext(IntlContext);
  return context.messages;
}

export function useTranslations(namespace?: string) {
  const context = React.useContext(IntlContext);

  return React.useCallback(
    (key: string, values?: TranslationValues) => {
      const scopedKey = namespace ? `${namespace}.${key}` : key;
      const value = lookup(context.messages, scopedKey);
      if (typeof value === "string") return interpolate(value, values);
      return scopedKey;
    },
    [context.messages, namespace],
  );
}

export { useLocaleRuntime } from "@/i18n/locale-runtime";
